import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../lib/api'
import { adminApi } from '../lib/adminApi'

/**
 * 관리자 로그인.
 *
 * 실패 사유를 "아이디가 없습니다 / 비밀번호가 틀립니다"로 나누지 않는다 —
 * 서버가 두 경우를 같은 응답으로 돌려주는 것과 같은 이유다(계정 존재 여부를 흘리지 않는다).
 *
 * **2026-08-19 개발자 원클릭 로그인을 없앴다**(dev-패널-기획 §15-2). dev 계정도 다른 계정과
 * 똑같이 이 폼으로 들어온다 — 비밀번호 없는 입구가 하나도 없는 상태가 인계(§10-3)에 안전하다.
 */
export function AdminLoginPage() {
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const login = useMutation({
    mutationFn: () => adminApi.login(loginId, password),
    onSuccess: (me) => {
      queryClient.setQueryData(['admin-me'], me)
      void navigate('/admin', { replace: true })
    },
  })

  const message =
    login.error instanceof ApiError
      ? login.error.status === 429
        ? '로그인 시도가 많습니다. 잠시 후 다시 시도해 주세요.'
        : login.error.status === 0
          ? login.error.detail[0]
          : '아이디 또는 비밀번호가 올바르지 않습니다.'
      : login.error
        ? '로그인 중 오류가 발생했습니다.'
        : null

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    login.mutate()
  }

  return (
    <div style={{ maxWidth: 380, margin: '48px auto' }}>
      <h1 className="page-title">관리자 로그인</h1>
      <p className="page-desc">운영자 계정으로만 접근할 수 있습니다.</p>

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="admin-login-id">아이디</label>
          <input
            id="admin-login-id"
            type="text"
            autoComplete="username"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="admin-password">비밀번호</label>
          <input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {message !== null && (
          <div className="notice error" role="alert">
            {message}
          </div>
        )}

        <button
          className="btn"
          type="submit"
          disabled={loginId.length < 3 || password.length === 0 || login.isPending}
        >
          {login.isPending ? '확인 중…' : '로그인'}
        </button>
      </form>
    </div>
  )
}
